import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'

const FACES = [
  { value: 0,  emoji: '😄', label: 'No symptoms',              color: '#085041' },
  { value: 2,  emoji: '🙂', label: 'A little bit',             color: '#1D9E75' },
  { value: 4,  emoji: '😐', label: 'A little more',            color: '#BA7517' },
  { value: 6,  emoji: '😟', label: 'Even more symptoms',       color: '#D85A30' },
  { value: 8,  emoji: '😢', label: 'A whole lot of symptoms',  color: '#A32D2D' },
  { value: 10, emoji: '😭', label: 'Worst possible symptoms',  color: '#791F1F' },
]

const DIMENSIONS = [
  {
    key: 'frequency_score',
    label: 'Frequency',
    description: 'How often do you have AFib episodes?',
    anchors: ['No episodes', 'Rare episodes', 'Occasional episodes', 'Frequent episodes', 'Very frequent', 'Constant episodes']
  },
  {
    key: 'duration_score',
    label: 'Duration',
    description: 'How long do your AFib episodes last?',
    anchors: ['No episodes', 'Seconds only', 'Minutes', 'Hours', 'Most of the day', 'Days at a time']
  },
  {
    key: 'severity_score',
    label: 'Severity',
    description: 'How intense are your AFib episodes when they occur?',
    anchors: ['No symptoms', 'Very mild', 'Mild', 'Moderate', 'Severe', 'Unbearable']
  },
  {
    key: 'symptom_score',
    label: 'Symptom burden',
    description: 'How much do symptoms affect your daily life? (palpitations, shortness of breath, fatigue, dizziness)',
    anchors: ['No impact', 'Barely notice', 'Minor impact', 'Moderate impact', 'Major impact', 'Cannot function']
  },
  {
    key: 'wellbeing_score',
    label: 'Global well-being',
    description: 'Overall, how is your quality of life with AFib?',
    anchors: ['Excellent', 'Very good', 'Good', 'Fair', 'Poor', 'Very poor']
  },
]

function FacePicker({ value, onChange, anchors }) {
  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        gap: 4, marginBottom: 8
      }}>
        {FACES.map((face, i) => (
          <button
            key={face.value}
            onClick={() => onChange(face.value)}
            style={{
              flex: 1, padding: '8px 2px', borderRadius: 10, border: 'none',
              background: value === face.value ? face.color : '#f0f4f8',
              cursor: 'pointer', display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 2,
              transform: value === face.value ? 'scale(1.08)' : 'scale(1)',
              transition: 'all 0.15s'
            }}
          >
            <span style={{ fontSize: 24 }}>{face.emoji}</span>
            <span style={{
              fontSize: 9, lineHeight: 1.2, textAlign: 'center',
              color: value === face.value ? 'white' : '#888',
              fontWeight: value === face.value ? 700 : 400
            }}>
              {anchors[i]}
            </span>
          </button>
        ))}
      </div>
      {value !== null && (
        <div style={{
          textAlign: 'center', fontSize: 12, marginTop: 4,
          color: FACES.find(f => f.value === value)?.color,
          fontWeight: 600
        }}>
          {FACES.find(f => f.value === value)?.label}
          <span style={{ color: '#888', fontWeight: 400 }}> — score: {value}/10</span>
        </div>
      )}
    </div>
  )
}

function totalLabel(total) {
  if (total <= 15) return { text: 'Mild — minimal impact on daily life', color: '#085041', bg: '#E1F5EE' }
  if (total <= 25) return { text: 'Moderate — noticeable but manageable', color: '#633806', bg: '#FAEEDA' }
  if (total <= 35) return { text: 'Moderate-severe — significant impact', color: '#993C1D', bg: '#FAECE7' }
  return { text: 'Severe — major impact on quality of life', color: '#A32D2D', bg: '#FCEBEB' }
}

export default function SeverityScale({ session, onComplete }) {
  const [assessmentType, setAssessmentType] = useState(null)
  const [scores, setScores] = useState({
    frequency_score: null,
    duration_score: null,
    severity_score: null,
    symptom_score: null,
    wellbeing_score: null,
  })
  const [saving, setSaving] = useState(false)
  const [history, setHistory] = useState([])
  const [view, setView] = useState('choose')

  useEffect(() => {
    fetchHistory()
  }, [])

  async function fetchHistory() {
    const { data } = await supabase
      .from('afib_severity')
      .select('*')
      .eq('user_id', session.user.id)
      .order('recorded_at', { ascending: true })
    setHistory(data || [])
  }

  function setScore(key, val) {
    setScores(s => ({ ...s, [key]: val }))
  }

  const allAnswered = Object.values(scores).every(v => v !== null)
  const total = allAnswered ? Object.values(scores).reduce((a, b) => a + b, 0) : null

  async function handleSubmit() {
    if (!allAnswered || !assessmentType) return
    setSaving(true)
    await supabase.from('afib_severity').insert({
      user_id: session.user.id,
      ...scores,
      assessment_type: assessmentType,
      recorded_at: new Date().toISOString().split('T')[0]
    })
    await fetchHistory()
    setSaving(false)
    setView('trend')
  }

  const typeColors = {
    baseline: '#185FA5',
    weekly: '#1D9E75',
    endpoint: '#534AB7'
  }

  const typeLabels = {
    baseline: 'Baseline',
    weekly: 'Weekly',
    endpoint: '2-month endpoint'
  }

  // ── CHOOSE VIEW ──────────────────────────────────────
  if (view === 'choose') {
    const hasBaseline = history.some(h => h.assessment_type === 'baseline')
    const hasEndpoint = history.some(h => h.assessment_type === 'endpoint')

    return (
      <div>
        <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#2C2C2C', marginBottom: 6 }}>
            AFib Severity Scale
          </div>
          <div style={{ fontSize: 13, color: '#666', lineHeight: 1.6, marginBottom: 16 }}>
            Rate each dimension of your AFib using the faces below. Lower scores mean fewer symptoms.
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            Select assessment type
          </div>

          {[
            ['baseline', '📋', 'Baseline assessment', 'Complete this when you first enroll', hasBaseline],
            ['weekly', '📅', 'Weekly check-in', 'Optional — track week to week changes', false],
            ['endpoint', '🏁', '2-month endpoint', 'Complete at the end of your program', hasEndpoint],
          ].map(([type, icon, label, sub, done]) => (
            <button key={type} onClick={() => { setAssessmentType(type); setView('assess') }} style={{
              width: '100%', padding: '14px 16px', borderRadius: 10, border: 'none',
              background: assessmentType === type ? typeColors[type] : '#f0f4f8',
              marginBottom: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left'
            }}>
              <span style={{ fontSize: 22 }}>{icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#2C2C2C' }}>{label}</div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{sub}</div>
              </div>
              {done && (
                <span style={{ fontSize: 11, background: '#E1F5EE', color: '#085041', padding: '2px 8px', borderRadius: 8, fontWeight: 600 }}>
                  Done ✓
                </span>
              )}
            </button>
          ))}
        </div>

        {history.length > 0 && (
          <button onClick={() => setView('trend')} style={{
            width: '100%', padding: 14, background: 'white', color: '#185FA5',
            border: '1.5px solid #185FA5', borderRadius: 10, fontSize: 14,
            fontWeight: 700, cursor: 'pointer'
          }}>
            📈 View my severity trend
          </button>
        )}
      </div>
    )
  }

  // ── ASSESSMENT VIEW ──────────────────────────────────
  if (view === 'assess') {
    return (
      <div>
        <button onClick={() => setView('choose')} style={{
          background: 'none', border: 'none', color: '#185FA5', fontSize: 13,
          fontWeight: 600, cursor: 'pointer', marginBottom: 12, padding: 0
        }}>
          ← Back
        </button>

        <div style={{ background: '#E6F1FB', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#0C447C' }}>
          <strong>{typeLabels[assessmentType]}</strong> — rate each dimension by selecting a face
        </div>

        {DIMENSIONS.map(dim => (
          <div key={dim.key} style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#2C2C2C', marginBottom: 4 }}>{dim.label}</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>{dim.description}</div>
            <FacePicker
              value={scores[dim.key]}
              onChange={val => setScore(dim.key, val)}
              anchors={dim.anchors}
            />
          </div>
        ))}

        {allAnswered && (
          <div style={{
            background: totalLabel(total).bg, borderRadius: 12,
            padding: '14px 16px', marginBottom: 14,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: totalLabel(total).color }}>
                Total severity score
              </div>
              <div style={{ fontSize: 12, color: totalLabel(total).color, marginTop: 2 }}>
                {totalLabel(total).text}
              </div>
            </div>
            <div style={{ fontSize: 32, fontWeight: 700, color: totalLabel(total).color }}>
              {total}<span style={{ fontSize: 14 }}>/50</span>
            </div>
          </div>
        )}

        <button onClick={handleSubmit} disabled={!allAnswered || saving} style={{
          width: '100%', padding: 14,
          background: !allAnswered ? '#ccc' : saving ? '#9CB8D9' : '#185FA5',
          color: 'white', border: 'none', borderRadius: 10, fontSize: 15,
          fontWeight: 700, cursor: !allAnswered ? 'not-allowed' : 'pointer'
        }}>
          {!allAnswered ? 'Please rate all 5 dimensions' : saving ? 'Saving...' : 'Save assessment'}
        </button>
      </div>
    )
  }

  // ── TREND VIEW ───────────────────────────────────────
  if (view === 'trend') {
    return (
      <div>
        <button onClick={() => setView('choose')} style={{
          background: 'none', border: 'none', color: '#185FA5', fontSize: 13,
          fontWeight: 600, cursor: 'pointer', marginBottom: 12, padding: 0
        }}>
          ← Back
        </button>

        <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#2C2C2C', marginBottom: 16 }}>
            Severity over time
          </div>

          {history.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#888', fontSize: 13, padding: '20px 0' }}>
              No assessments yet. Complete your first one to start tracking.
            </div>
          ) : (
            history.map((entry, i) => {
              const entryTotal = entry.frequency_score + entry.duration_score +
                entry.severity_score + entry.symptom_score + entry.wellbeing_score
              const pct = Math.round((entryTotal / 50) * 100)
              const label = totalLabel(entryTotal)
              const prev = i > 0 ? history[i - 1] : null
              const prevTotal = prev ? prev.frequency_score + prev.duration_score +
                prev.severity_score + prev.symptom_score + prev.wellbeing_score : null
              const change = prevTotal !== null ? entryTotal - prevTotal : null

              return (
                <div key={entry.id} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: i < history.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 8, fontWeight: 600,
                        background: typeColors[entry.assessment_type] + '22',
                        color: typeColors[entry.assessment_type]
                      }}>
                        {typeLabels[entry.assessment_type]}
                      </span>
                      <span style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>{entry.recorded_at}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {change !== null && (
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          color: change < 0 ? '#085041' : change > 0 ? '#A32D2D' : '#888'
                        }}>
                          {change < 0 ? '▼ ' + Math.abs(change) : change > 0 ? '▲ +' + change : '— no change'}
                        </span>
                      )}
                      <span style={{ fontSize: 20, fontWeight: 700, color: label.color }}>
                        {entryTotal}<span style={{ fontSize: 12 }}>/50</span>
                      </span>
                    </div>
                  </div>

                  <div style={{ height: 8, background: '#f0f0f0', borderRadius: 4, marginBottom: 6 }}>
                    <div style={{ width: `${pct}%`, height: 8, background: label.color, borderRadius: 4, transition: 'width 0.4s' }} />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {['Frequency', 'Duration', 'Severity', 'Symptoms', 'Well-being'].map((dim, j) => {
                      const val = [entry.frequency_score, entry.duration_score, entry.severity_score, entry.symptom_score, entry.wellbeing_score][j]
                      const face = FACES.reduce((prev, curr) => Math.abs(curr.value - val) < Math.abs(prev.value - val) ? curr : prev)
                      return (
                        <div key={dim} style={{ textAlign: 'center', flex: 1 }}>
                          <div style={{ fontSize: 18 }}>{face.emoji}</div>
                          <div style={{ fontSize: 9, color: '#888' }}>{dim}</div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: face.color }}>{val}</div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })
          )}
        </div>

        <button onClick={() => setView('choose')} style={{
          width: '100%', padding: 14, background: '#185FA5',
          color: 'white', border: 'none', borderRadius: 10, fontSize: 14,
          fontWeight: 700, cursor: 'pointer'
        }}>
          + New assessment
        </button>
      </div>
    )
  }
}